import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CommunityMessage,
  fetchCurrentCommunityMessages,
} from '../../../backendServices/communityManagment/api';

/**
 * Relevé des messages de la communauté, et mémoire de ce que CE poste a déjà lu.
 *
 * Pourquoi un relevé et non une socket : admin-front n'ouvre de WebSocket que
 * dans la régie, et pour un flux vidéo. Tenir une socket de plus par poste
 * connecté, en permanence, pour trois lignes de texte par semaine, coûterait
 * plus cher que ce que ça rapporte — un message de service n'a pas besoin
 * d'arriver à la milliseconde.
 *
 * La lecture est PROPRE À CHAQUE POSTE, dans le localStorage : deux personnes
 * derrière le même compte ne se ferment pas mutuellement le bandeau, et rien de
 * cet état ne remonte au serveur.
 */
const INTERVALLE_MS = 30_000;
const CLE_LECTURE   = 'communityMessagesRead';

const lireIdsLus = (): number[] => {
  try {
    const brut = localStorage.getItem(CLE_LECTURE);
    const ids  = brut ? JSON.parse(brut) : [];
    return Array.isArray(ids) ? ids.filter((i: unknown) => typeof i === 'number') : [];
  } catch {
    // Navigation privée, stockage refusé, valeur abîmée : on repart de rien
    // plutôt que d'empêcher le bandeau de s'afficher.
    return [];
  }
};

const ecrireIdsLus = (ids: number[]): void => {
  try { localStorage.setItem(CLE_LECTURE, JSON.stringify(ids)); } catch { /* sans conséquence */ }
};

export interface CommunityMessagesState {
  /** Tous les messages vivants, le plus récent d'abord. */
  messages : CommunityMessage[];
  /**
   * Ceux que ce poste n'a pas encore rangés — ce qui décide de l'affichage. Un
   * message « Prioritaire » y reste même lu : il ne part qu'à son retrait.
   */
  unread   : CommunityMessage[];
  /** Ce que ce poste a déjà lu (« J'ai lu ») — pour marquer un prioritaire lu. */
  readIds  : number[];
  markRead : (id: number) => void;
  refresh  : () => void;
}

export const useCommunityMessages = (enabled: boolean, adminId: number | null): CommunityMessagesState => {
  const [messages, setMessages] = useState<CommunityMessage[]>([]);
  const [readIds , setReadIds ] = useState<number[]>(lireIdsLus);

  // Le relevé est rejoué par plusieurs chemins (minuterie, retour sur l'onglet,
  // envoi qu'on vient de faire) : sans ce garde, deux appels peuvent se croiser
  // et le plus lent écraserait le plus récent.
  const enVolRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled || enVolRef.current) return;
    enVolRef.current = true;
    try {
      const recus = await fetchCurrentCommunityMessages(adminId);
      setMessages(recus);

      // Purge de la mémoire de lecture : un identifiant qui n'est plus servi
      // désigne un message retiré ou échu. Le garder ferait grossir la clé
      // indéfiniment, pour rien.
      setReadIds(prev => {
        const vivants = new Set(recus.map(m => m.id));
        const gardes  = prev.filter(id => vivants.has(id));
        if (gardes.length !== prev.length) ecrireIdsLus(gardes);
        return gardes.length === prev.length ? prev : gardes;
      });
    } catch {
      // Un relevé raté n'efface rien : l'API peut être momentanément absente,
      // et vider le bandeau sur une erreur réseau serait pire que de le laisser.
    } finally {
      enVolRef.current = false;
    }
  }, [enabled, adminId]);

  useEffect(() => {
    if (!enabled) { setMessages([]); return; }

    refresh();
    const minuterie = setInterval(refresh, INTERVALLE_MS);

    // Les minuteries d'un onglet en arrière-plan sont ralenties, voire gelées :
    // le retour à l'écran est le moment où l'on veut être à jour.
    const auRetour = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', auRetour);

    return () => {
      clearInterval(minuterie);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [enabled, refresh]);

  const markRead = useCallback((id: number) => {
    setReadIds(prev => {
      if (prev.includes(id)) return prev;
      const suivant = [...prev, id];
      ecrireIdsLus(suivant);
      return suivant;
    });
  }, []);

  return {
    messages,
    unread : messages.filter(m => m.priority === true || !readIds.includes(m.id)),
    readIds,
    markRead,
    refresh,
  };
};

export default useCommunityMessages;
